import React, { useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { useFilters } from "@/state/FilterContext";

export default function AiInsightsPanel() {
  const { queryParams } = useFilters();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  const run = () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setText("");
    setLoading(true);
    const qp = queryParams();
    const usp = new URLSearchParams();
    Object.entries(qp).forEach(([k, v]) => usp.set(k, String(v)));
    fetch(`${API}/dashboard/ai-insights?${usp.toString()}`, { signal: ctrl.signal })
      .then(async (res) => {
        if (!res.body) throw new Error("Stream not supported");
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const payload = line.slice(6);
            if (payload === "[DONE]") {
              setLoading(false);
              return;
            }
            setText((prev) => prev + payload);
          }
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    run();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line
  }, [JSON.stringify(queryParams())]);

  return (
    <div
      data-testid="ai-insights"
      className="bg-[#F0F7FF] border border-[#BAE6FD] rounded-lg p-5 relative overflow-hidden fade-up"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-md bg-white border border-[#BAE6FD] flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-[#0070F2]" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-base font-semibold text-slate-800">AI Destekli İçgörüler</h3>
            <span className="text-[10px] uppercase tracking-[0.08em] font-bold text-[#0070F2] bg-white border border-[#BAE6FD] rounded px-1.5 py-0.5">
              Claude Sonnet
            </span>
            {loading && (
              <span className="inline-flex items-center gap-1.5 text-xs text-[#0070F2]">
                <span className="w-1.5 h-1.5 bg-[#0070F2] rounded-full pulse-dot" /> Üretiliyor…
              </span>
            )}
          </div>
          <div
            data-testid="ai-insights-text"
            className="text-sm leading-relaxed text-slate-800 mt-2 space-y-1.5 min-h-[60px]"
          >
            {text
              ? text.split("\n").filter((l) => l.trim().length > 0).map((line, idx) => {
                  const stripped = line.replace(/^#+\s*/, "").replace(/^[-*]\s+/, "");
                  const isHeader = /^#+\s/.test(line);
                  const isBullet = /^[-*]\s+/.test(line);
                  const parts = stripped.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
                    p.startsWith("**") && p.endsWith("**") ? (
                      <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
                    ) : (
                      <React.Fragment key={i}>{p}</React.Fragment>
                    )
                  );
                  if (isHeader) {
                    return (
                      <div key={idx} className="font-display text-sm font-semibold text-slate-900 pt-1">
                        {parts}
                      </div>
                    );
                  }
                  if (isBullet) {
                    return (
                      <div key={idx} className="flex gap-2">
                        <span className="text-[#0070F2] mt-1.5">•</span>
                        <span className="flex-1">{parts}</span>
                      </div>
                    );
                  }
                  return <p key={idx}>{parts}</p>;
                })
              : (loading ? null : <span className="text-slate-500">Filtrelere göre içgörüler oluşturmak için yenileyin.</span>)}
          </div>
        </div>
        <Button
          data-testid="ai-insights-refresh"
          variant="ghost"
          size="sm"
          onClick={run}
          disabled={loading}
          className="text-[#0054E3] hover:bg-white"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Yenile
        </Button>
      </div>
    </div>
  );
}
